import React from "react";
import { Box, Text, createStyles, useMantineTheme } from "@mantine/core";
import { Chart } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  LineController, // ← ✅ Add this
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  LineController, // ← ✅ Add this
  Title,
  Tooltip,
  Legend
);

const useChartStyles = createStyles((theme) => ({
  card: {
    backgroundColor: theme.white,
    borderRadius: theme.radius.md,
    boxShadow: theme.shadows.sm,
    padding: theme.spacing.lg,
    flex: 1,
  },
  chartBox: {
    position: "relative",
    height: 360,
    width: "100%",
  },
}));

interface YearlyStatusChartProps {
  years: string[];
  workingHours: number[];
  progress: number[];
}

export function YearlyStatusChart({
  years,
  workingHours,
  progress,
}: YearlyStatusChartProps) {
  const { classes } = useChartStyles();
  const theme = useMantineTheme();
  const data: ChartData<"bar" | "line", number[], string> = {
    labels: years,
    datasets: [
      {
        type: "bar" as const,
        label: "Working Hours",
        data: workingHours,
        backgroundColor: "#4B5BFF",
        yAxisID: "y",
        borderRadius: 4,
        barThickness: 24,
      },
      {
        type: "line" as const,
        label: "Employees Progress",
        data: progress,
        borderColor: "#FFA500",
        backgroundColor: "transparent",
        yAxisID: "y1",
        tension: 0.4,
        pointBackgroundColor: "#FFA500",
        pointBorderColor: "#FFA500",
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  };

  const options: ChartOptions<"bar" | "line"> = {
    maintainAspectRatio: false,
    responsive: true,
    layout: { padding: { top: 16, right: 24, bottom: 0, left: 0 } },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 24,
          boxWidth: 10,
          font: { size: 12, weight: "500" },
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      x: {
        stacked: false,
        grid: { display: false },
        ticks: {
          font: { size: 12, weight: "500" },
          color: theme.colors.dark[7],
        },
      },
      y: {
        type: "linear",
        position: "left",
        stacked: false,
        title: {
          display: true,
          text: "Working Hours",
          font: { size: 14, weight: "500" },
        },
        grid: { color: "#E0E0E0" },
        ticks: { font: { size: 12 }, color: theme.colors.gray[7] },
        min: 0,
      },
      y1: {
        type: "linear",
        position: "right",
        stacked: false,
        title: {
          display: true,
          text: "Employees Progress",
          font: { size: 14, weight: "500" },
        },
        grid: { drawOnChartArea: false },
        ticks: { font: { size: 12 }, color: theme.colors.gray[7] },
        min: 0,
      },
    },
  };

  return (
    <Box className={classes.card}>
      <Text weight={500} size="lg" mb={16}>
        Employees Yearly Status
      </Text>
      <Box className={classes.chartBox}>
        <Chart<"bar" | "line"> type="bar" data={data} options={options} />
      </Box>
    </Box>
  );
}
